import { useTranslation } from 'react-i18next';
import { Alert, Details, Heading, Paragraph, Skeleton, Tag } from '@digdir/designsystemet-react';
import {
  ChatExclamationmarkIcon,
  CheckmarkCircleIcon,
  ExclamationmarkTriangleIcon,
  HandshakeIcon,
  HeartIcon,
  PersonGroupIcon,
} from '@navikt/aksel-icons';
import type { HealthCategoryKey } from '../../../utils/healthCategory';
import { agreementOf } from '../../../domain/psychological-safety/team-result';
import {
  agreementTones,
  formatScore,
  getScoreLevel,
  levelTones,
  MAX_SCORE,
} from '../../../components/AnalysisLayout/analysis.utils';
import type { Area } from '../dashboard.utils';
import classes from '../TeamDashboard.module.css';

const icons: Record<HealthCategoryKey, typeof HeartIcon> = {
  inclusionAndRespect: PersonGroupIcon,
  safetyAndOpenness: ChatExclamationmarkIcon,
  recognitionAndAppreciation: HeartIcon,
  trustAndSupport: HandshakeIcon,
};

/** Rekkefølgen områdene vises i før teamet har data */
const areaKeys = Object.keys(icons) as HealthCategoryKey[];

const AreaIcon = ({ areaKey }: { areaKey: HealthCategoryKey | null }) => {
  const Icon = areaKey ? icons[areaKey] : HeartIcon;
  return (
    <span aria-hidden="true" className={classes.areaIcon}>
      <Icon />
    </span>
  );
};

const AreaCard = ({ area }: { area: Area }) => {
  const { t } = useTranslation();
  const level = getScoreLevel(area.score);
  const tone = levelTones[level];
  const agreement = agreementOf(area.standardDeviation);
  const agreementTone = agreement ? agreementTones[agreement] : null;
  const AgreementIcon = agreementTone === 'success' ? CheckmarkCircleIcon : ExclamationmarkTriangleIcon;

  return (
    <li className={classes.areaCard}>
      <div className={classes.areaTop}>
        <AreaIcon areaKey={area.key} />
        <Tag data-size="sm" data-color={tone}>
          {t(`analysis.level.${level}`)}
        </Tag>
      </div>
      <Heading level={3} data-size="xs">
        {area.key ? t(`dashboard.areas.${area.key}.name`) : area.name}
      </Heading>
      <div className={classes.areaScore}>
        <span aria-hidden="true" className={classes.areaNumber}>
          {formatScore(area.score, 2)}
        </span>
        <span className={classes.srOnly}>{t('analysis.scoreAria', { score: formatScore(area.score, 2) })}</span>
        <span aria-hidden="true" className={classes.track}>
          <span
            className={classes.fill}
            data-tone={tone}
            style={{ width: `${Math.min(100, (area.score / MAX_SCORE) * 100)}%` }}
          />
        </span>
      </div>
      <Paragraph data-size="sm" className={classes.subtle}>
        {area.key ? t(`dashboard.areas.${area.key}.description`) : area.description}
      </Paragraph>
      {agreement && (
        <Paragraph data-size="sm" className={classes.agreement} data-tone={agreementTone!}>
          <AgreementIcon aria-hidden className={classes.agreementIcon} />
          <span>
            <strong>{t(`teamResult.agreement.${agreement}`)}.</strong> {t(`teamResult.agreementExplanation.${agreement}`)}
          </span>
        </Paragraph>
      )}
    </li>
  );
};

/** Før første måling: de fire områdene uten tall */
const EmptyAreaCard = ({ areaKey }: { areaKey: HealthCategoryKey }) => {
  const { t } = useTranslation();
  return (
    <li className={classes.areaCard}>
      <div className={classes.areaTop}>
        <AreaIcon areaKey={areaKey} />
      </div>
      <Heading level={3} data-size="xs">
        {t(`dashboard.areas.${areaKey}.name`)}
      </Heading>
      <Paragraph data-size="sm" className={classes.subtle}>
        {t(`dashboard.areas.${areaKey}.description`)}
      </Paragraph>
      <Paragraph data-size="sm" className={classes.agreement} data-tone="neutral">
        <span>
          <strong>{t('dashboard.teamHealth.noData')}.</strong> {t('dashboard.teamHealth.noDataDescription')}
        </span>
      </Paragraph>
    </li>
  );
};

type AreaCardsProps = {
  areas: Area[];
  isLoading: boolean;
  error: Error | null;
};

/** De fire områdene, lavest score først */
export const AreaCards = ({ areas, isLoading, error }: AreaCardsProps) => {
  const { t } = useTranslation();

  return (
    <section aria-labelledby="areas-heading" className={classes.section}>
      <div className={classes.sectionHeader}>
        <div className={classes.heading}>
          <span className={classes.eyebrow}>{t('dashboard.areas.eyebrow')}</span>
          <Heading id="areas-heading" level={2} data-size="md">
            {t('dashboard.areas.title')}
          </Heading>
        </div>
        <Paragraph data-size="sm" className={classes.subtle}>
          {areas.length > 0 ? t('dashboard.areas.hint') : t('dashboard.teamHealth.descriptionNoData')}
        </Paragraph>
      </div>

      {isLoading ? (
        <div aria-busy="true" className={classes.areaGrid}>
          {areaKeys.map((key) => (
            <Skeleton key={key} variant="rectangle" width="100%" height={260} />
          ))}
        </div>
      ) : error ? (
        <Alert data-color="danger">
          <Paragraph>{error.message}</Paragraph>
        </Alert>
      ) : (
        <ul className={classes.areaGrid}>
          {areas.length > 0
            ? areas.map((area) => <AreaCard key={area.id} area={area} />)
            : areaKeys.map((key) => <EmptyAreaCard key={key} areaKey={key} />)}
        </ul>
      )}

      <Details>
        <Details.Summary>{t('dashboard.areas.explainTitle')}</Details.Summary>
        <Details.Content>{t('dashboard.areas.explain')}</Details.Content>
      </Details>
    </section>
  );
};
